import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import defaultImage from "../../../Common/productDefaultImage.png";
import {
  addItemToWishlist,
  isWishlistItemExist,
  removeWishlistItem,
} from "../../State/Wishlist/Action";

const productImage = (product) =>
  product?.images?.find((image) => image?.isPrimary)?.fullUrl ||
  product?.images?.[0]?.fullUrl ||
  defaultImage;

const cheapestVariant = (product) =>
  (product?.variants || []).reduce(
    (min, curr) => (curr?.adminPrice < min?.adminPrice ? curr : min),
    product?.variants?.[0]
  );

function SuggestProductCard({ product, onOpen, onNeedAuth }) {
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const role = localStorage.getItem("role");
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [loadingProductId, setLoadingProductId] = useState(null);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  const { wishlist } = useSelector((store) => store);
  const { convert, customerPriceWithCharge, payableAmount, selectedCurrency } =
    useConvertPrice();

  useEffect(() => {
    if (jwt && role === "CUSTOMER" && product.id) {
      dispatch(isWishlistItemExist(product.id)).then((exists) => {
        if (typeof exists === "boolean") setIsInWishlist(exists);
      });
    }
  }, [
    dispatch,
    product.id,
    jwt,
    role,
    wishlist?.deletedWishlistItem,
    wishlist?.addWislistItem,
  ]);

  const handleFavorite = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setLoadingProductId(product.id);
    try {
      if (!jwt || role !== "CUSTOMER") {
        sessionStorage.setItem("pendingWishlistProduct", product.id);
        onNeedAuth();
        return;
      }
      if (isInWishlist) {
        await dispatch(removeWishlistItem(product.id));
        setIsInWishlist(false);
      } else {
        await dispatch(addItemToWishlist(product.id));
        setIsInWishlist(true);
      }
    } finally {
      setLoadingProductId(null);
    }
  };

  const variant = cheapestVariant(product);
  const hasDiscount = variant?.viewTotalDiscount > 0;
  const originalPrice = variant
    ? convert(customerPriceWithCharge(selectedCurrency, variant.viewCustomerAmount))
    : null;
  const salePrice = variant
    ? payableAmount(
        customerPriceWithCharge(selectedCurrency, variant.viewCustomerAmount),
        variant.viewTotalDiscount
      )
    : null;

  return (
    <button
      type="button"
      className="w-full border-0 bg-transparent p-0 text-left"
      onClick={() => onOpen(product.id)}
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-[#f6f2ed]">
        <img
          src={productImage(product)}
          alt={product.title}
          className="h-full w-full object-cover"
        />
        <span
          role="button"
          tabIndex={0}
          onClick={handleFavorite}
          onKeyDown={(e) => e.key === "Enter" && handleFavorite(e)}
          className="group/heart absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-white/95"
        >
          {loadingProductId === product.id && wishlistLoading ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-stone-400 border-t-transparent" />
          ) : isInWishlist ? (
            <Favorite className="text-red-500" style={{ fontSize: 18 }} />
          ) : (
            <FavoriteBorder
              className="text-stone-400 group-hover/heart:text-red-500"
              style={{ fontSize: 18 }}
            />
          )}
        </span>
      </div>
      <h3
        className="mt-2.5 truncate text-[14px] italic leading-5 text-[#333]"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {product?.brand?.name || "Brand"}
      </h3>
      <p className="mt-0.5 truncate text-[13px] font-light leading-5 text-[#707070]">
        {product?.title}
      </p>
      {variant && (
        <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-[13px]">
          <span className="font-semibold text-[#333]">
            {salePrice} {selectedCurrency}
          </span>
          {hasDiscount && (
            <>
              <span className="text-[#9a9a9a] line-through">{originalPrice}</span>
              <span className="font-medium text-[#2f9e44]">
                {variant.viewTotalDiscount}% OFF
              </span>
            </>
          )}
        </div>
      )}
    </button>
  );
}

export default function SearchSuggestPanel({
  query,
  categories,
  products,
  onSearch,
  onOpenProduct,
  onNeedAuth,
}) {
  const navigate = useNavigate();

  const topSearches = useMemo(() => {
    const names = [];
    const seen = new Set();
    (categories || []).forEach((category) => {
      (category.subCategories || []).forEach((sub) => {
        (sub.productCategories || []).forEach((item) => {
          const name = item?.name?.trim();
          if (!name || seen.has(name.toLowerCase())) return;
          seen.add(name.toLowerCase());
          names.push(name);
        });
      });
    });
    (products || []).forEach((product) => {
      const brand = product?.brand?.name?.trim();
      if (brand && !seen.has(brand.toLowerCase())) {
        seen.add(brand.toLowerCase());
        names.push(brand);
      }
    });
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? names.filter((name) => name.toLowerCase().includes(needle))
      : names;
    return filtered.slice(0, 12);
  }, [categories, products, query]);

  const topProducts = useMemo(() => {
    const list = products || [];
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? list.filter((product) => {
          const haystack = [
            product?.title,
            product?.brand?.name,
            product?.productCategory?.name,
            product?.category?.name,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(needle);
        })
      : list;
    return filtered.slice(0, 12);
  }, [products, query]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 min-[700px]:flex-row min-[700px]:gap-8">
      <div className="shrink-0 min-[700px]:w-[210px]">
        <p className="mb-3 text-[13px] font-medium text-[#8a8a8a]">Top Searches</p>
        <div className="flex flex-wrap gap-2">
          {topSearches.length === 0 ? (
            <p className="text-[13px] font-light text-[#9a9a9a]">No matching searches</p>
          ) : (
            topSearches.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => onSearch(term)}
                className="rounded-full border-0 bg-[#f4f1ee] px-3.5 py-1.5 text-[13px] font-light text-[#333] hover:bg-[#ece7e1]"
              >
                {term}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <p className="mb-3 shrink-0 text-[15px] font-medium text-[#222]">Top Products</p>
        {topProducts.length === 0 ? (
          <p className="py-10 text-[13px] font-light text-[#9a9a9a]">No products to show</p>
        ) : (
          <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {topProducts.map((product) => (
                <SuggestProductCard
                  key={product.id}
                  product={product}
                  onOpen={(id) => {
                    onOpenProduct();
                    navigate(`/product/${id}`);
                  }}
                  onNeedAuth={onNeedAuth}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
